import { useCallback, useMemo, useState } from "react"
import {
  Business,
  Contact,
  Household,
  isTaskableBusiness,
  isTaskableContact,
  isTaskableHousehold,
  Tag,
  Task,
  TaskPriority,
} from "@/schema"
import { formatDateTimeShort, isValidDate, toValidDate, toZonedDate } from "@/utils/format-date"
import { useQueryClient } from "@tanstack/react-query"
import {
  BaseInlineInput,
  InlineAssignmentSelect,
  InlineDatePicker,
  InlineRelationshipSelect,
  InlineSelect,
  ReadonlyAttribute,
} from "@ui/forms/inline"
import { MaskedIcon } from "@ui/masked-icon"
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from "@ui/shad"
import { cn } from "@ui/utils"
import {
  Book,
  CalendarIcon,
  CheckCircle,
  Flag,
  FolderKanban,
  Link2,
  Link as LinkIcon,
  Lock,
  Plus,
  Repeat,
  Tag as TagIcon,
  Unlock,
  User,
  X,
} from "lucide-react"
import { toast } from "sonner"

import { useFetchHouseholdProjects, useInfiniteProjectsWithFilters } from "@/hooks/api/projects/queries"
import { usePropagateRecurrence } from "@/hooks/api/tasks"
import { useBookSelectorVisibility } from "@/hooks/use-book-selector-visibility"
import { useBookTimezone } from "@/hooks/use-book-timezone"
import { InlineMentionEditor } from "@/components/cross-references/inline-mention-editor"
import { ConfirmDialog } from "@/components/dialogs"
import PeekView from "@/components/peek-view"
import { TaskRepeatBadge } from "@/components/tasks/repeat-badge"
import { TaskLabelSelector } from "@/components/tasks/shared/task-label-selector"
import { dateAtStartOfDayInTimeZone } from "@/components/tasks/utils/deadline-payload"
import { UserAvatar } from "@/components/users/user-avatar"

import { MakeRecurringButton } from "./make-recurring-button"
import TaskAttachments from "./task-attachments"

const getPriorityIcon = (priority: TaskPriority) => {
  switch (priority) {
    case "low":
      return <MaskedIcon src="/icons/low-priority.svg" className="size-3.5" />
    case "medium":
      return <MaskedIcon src="/icons/medium-priority.svg" className="size-3.5" />
    case "high":
      return <MaskedIcon src="/icons/high-priority.svg" className="size-3.5" />
    case "urgent":
      return (
        <div className="bg-icon flex size-3.5 items-center justify-center rounded-sm text-xs font-medium text-white">
          !
        </div>
      )
    default:
      return <Flag className="h-4 w-4" />
  }
}

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
]

interface TaskMetadataProps {
  task: Task
  isCompleted: boolean
  completedAt?: Date | null
  onUpdateTask?: (payload: Partial<Task> & { id: string }, options?: { onSuccess?: () => void }) => void | Promise<void>
}

export default function TaskMetadata({ task, isCompleted, completedAt, onUpdateTask }: TaskMetadataProps) {
  const queryClient = useQueryClient()
  const { shouldShow: isAllBooksMode } = useBookSelectorVisibility()
  const bookTimezone = useBookTimezone(task.book_id)
  const [showDueTime, setShowDueTime] = useState(task.all_day === false)
  const [isProjectPopoverOpen, setIsProjectPopoverOpen] = useState(false)
  const [projectSearch, setProjectSearch] = useState("")
  const [showRemoveProjectConfirm, setShowRemoveProjectConfirm] = useState(false)
  const [showApplyConfirm, setShowApplyConfirm] = useState(false)
  const propagateRecurrenceMutation = usePropagateRecurrence()

  const getTaskable = (): { household?: Household; contact?: Contact; business?: Business } => {
    if (task.taskable) {
      if (isTaskableHousehold(task.taskable, task.taskable_type ?? undefined)) {
        return { household: task.taskable }
      }
      if (isTaskableContact(task.taskable, task.taskable_type ?? undefined)) {
        return { contact: task.taskable }
      }
      if (isTaskableBusiness(task.taskable, task.taskable_type ?? undefined)) {
        return { business: task.taskable }
      }
    }
    // Fallback to legacy fields
    return {
      household: task.household,
      contact: task.contact,
    }
  }

  const { household, contact, business } = getTaskable()

  const { data: householdProjectsData } = useFetchHouseholdProjects(household?.id ?? "", {
    enabled: isProjectPopoverOpen && !!household?.id,
  })

  const { data: noHouseholdProjectsData } = useInfiniteProjectsWithFilters({
    searchTerm: projectSearch,
    filters: [],
    enabled: isProjectPopoverOpen && !household?.id,
    bookIdOverride: isAllBooksMode ? task.book_id : undefined,
    withoutHousehold: true,
  })

  const activeProjects = useMemo(() => {
    const projects = household?.id ? householdProjectsData?.projects : noHouseholdProjectsData?.projects
    return projects?.filter((p) => p.status === "in_progress" || p.status === "overdue") ?? []
  }, [household?.id, householdProjectsData?.projects, noHouseholdProjectsData?.projects])
  const createdAt = useMemo(() => toValidDate(task.created_at), [task.created_at])
  const updatedAt = useMemo(() => toValidDate(task.updated_at), [task.updated_at])
  const completedAtDate = toValidDate(completedAt)
  const completedValue = [
    completedAtDate ? formatDateTimeShort(completedAtDate) : null,
    task.completed_by ? `by ${task.completed_by.full_name}` : null,
  ]
    .filter(Boolean)
    .join(" ")

  // All-day deadlines anchor to the book's timezone (stored at 23:00 book-local; see
  // Task#adjust_deadline_for_all_day), so zone the displayed value to the book timezone for
  // viewers east of it. Timed deadlines keep their browser-local moment.
  const dueDateValue = useMemo(() => {
    const deadline = toValidDate(task.deadline_at)
    if (!deadline) return null
    return showDueTime ? deadline : toZonedDate(deadline, bookTimezone)
  }, [task.deadline_at, showDueTime, bookTimezone])

  const updateTask = useCallback(
    (payload: Partial<Task> & { id: string }) => {
      const promise = onUpdateTask?.(payload, {
        onSuccess: () => {
          toast.success("Task updated")
          queryClient.invalidateQueries({ queryKey: ["tasks", "grouped"] })
          queryClient.invalidateQueries({ queryKey: ["tasks", "household"] })
          queryClient.invalidateQueries({ queryKey: ["tasks", "business"] })
          queryClient.invalidateQueries({ queryKey: ["projects"] })
          queryClient.invalidateQueries({ queryKey: ["project"] })
        },
      })
      promise?.catch(() => undefined)
      return promise
    },
    [onUpdateTask, queryClient]
  )

  const handleProjectChange = (projectId: string | null, milestoneId: string | null) => {
    updateTask({
      id: task.id!,
      project_id: projectId,
      project_milestone_id: milestoneId,
    })
  }

  const handleRemoveFromProject = useCallback(() => {
    updateTask({
      id: task.id!,
      project_id: null,
      project_milestone_id: null,
    })
    setShowRemoveProjectConfirm(false)
  }, [task.id, updateTask])

  const currentLabel = useMemo(() => {
    const currentLabelId = task.tag_ids?.[0] ?? task.tags?.[0]?.id ?? null
    if (!currentLabelId) return null
    return task.tags?.find((tag) => tag.id === currentLabelId) || null
  }, [task.tags, task.tag_ids])

  const handlePriorityChange = (value: TaskPriority | null) => {
    updateTask({ id: task.id!, priority: value })
  }

  const handleVisibilityChange = (value: "private" | "public" | null) => {
    updateTask({ id: task.id!, is_private: value === "private" })
  }

  const handleLabelChange = (tag: Tag | null) => {
    updateTask({ id: task.id!, tag_ids: tag ? [tag.id] : [] })
  }

  const handlePropagateRecurrence = useCallback(() => {
    if (!task.id) return

    propagateRecurrenceMutation.mutate(task.id, {
      onSuccess: () => {
        toast.success("Changes applied to series")
        setShowApplyConfirm(false)
      },
    })
  }, [task.id, propagateRecurrenceMutation])

  return (
    <div className="flex w-full flex-col gap-1">
      {isAllBooksMode && task.book_name && (
        <ReadonlyAttribute label="Book" LabelIcon={Book} value={task.book_name} fixedLabelWidth="120px" />
      )}
      <InlineSelect<TaskPriority>
        label="Priority"
        LabelIcon={Flag}
        value={task.priority ?? null}
        options={PRIORITY_OPTIONS.map((option) => ({
          value: option.value,
          label: (
            <div className="flex items-center gap-2">
              <div className="text-icon">{getPriorityIcon(option.value)}</div>
              <span>{option.label}</span>
            </div>
          ),
        }))}
        placeholder="-"
        clearLabel="No priority"
        onSave={handlePriorityChange}
        disabled={isCompleted}
        fixedLabelWidth="120px"
      />
      <InlineSelect<"private" | "public">
        label="Visibility"
        LabelIcon={task.is_private ? Lock : Unlock}
        value={task.is_private ? "private" : "public"}
        options={[
          {
            value: "public",
            label: (
              <div className="flex items-center gap-2">
                <Unlock className="size-3.5" />
                <span>Visible to everyone</span>
              </div>
            ),
          },
          {
            value: "private",
            label: (
              <div className="flex items-center gap-2">
                <Lock className="size-3.5" />
                <span>Private</span>
              </div>
            ),
          },
        ]}
        placeholder="Visible to everyone"
        onSave={handleVisibilityChange}
        disabled={isCompleted}
        fixedLabelWidth="120px"
        clearable={false}
      />
      <BaseInlineInput label="Label" LabelIcon={TagIcon} fixedLabelWidth="120px">
        <TaskLabelSelector
          label={currentLabel}
          onLabelChange={handleLabelChange}
          disabled={isCompleted}
          className="w-full"
          triggerButton={
            <button
              type="button"
              className={cn(
                "focus:outline-primary flex h-8 w-full grow items-center justify-between rounded-md border-1 border-transparent px-2 text-left shadow-none focus:outline-1 md:text-sm",
                !currentLabel ? "text-input-placeholder" : "",
                isCompleted ? "cursor-default opacity-50" : "hover:bg-muted cursor-pointer"
              )}
              disabled={isCompleted}
            >
              {currentLabel ? (
                <div className="flex items-center gap-2">
                  <div
                    className="size-1.5 flex-none rounded-full"
                    style={{ backgroundColor: currentLabel.color || "var(--color-secondary)" }}
                  />
                  <span>{currentLabel.name}</span>
                </div>
              ) : (
                <span className="text-input-placeholder">-</span>
              )}
            </button>
          }
          contentProps={{ align: "start", className: "w-full" }}
        />
      </BaseInlineInput>
      <InlineAssignmentSelect
        label="Assigned to"
        LabelIcon={User}
        selectedUserId={task.assigned_to_id || null}
        selectedTeamId={task.assigned_team_id || null}
        onSave={(userId, teamId, team) => {
          if (teamId && team) {
            const teamMemberIds = team.users?.map((u) => u.id) ?? []
            const existingCollaboratorIds = task.collaborator_ids ?? []
            const mergedCollaboratorIds = Array.from(new Set([...existingCollaboratorIds, ...teamMemberIds]))

            updateTask({
              id: task.id!,
              assigned_team_id: teamId,
              assigned_to_id: null,
              collaborator_ids: mergedCollaboratorIds,
            })
          } else if (userId) {
            updateTask({
              id: task.id!,
              assigned_to_id: userId,
              assigned_team_id: null,
            })
          } else {
            updateTask({
              id: task.id!,
              assigned_to_id: null,
              assigned_team_id: null,
            })
          }
        }}
        isDisabled={isCompleted}
        fixedLabelWidth="120px"
      />
      <ReadonlyAttribute
        label="Created by"
        LabelIcon={User}
        fixedLabelWidth="120px"
        renderValue={() =>
          task.created_by?.id ? (
            <UserAvatar user={task.created_by} size="sm" showName variant="pill" />
          ) : (
            <span className="text-input-placeholder">-</span>
          )
        }
      />
      <InlineRelationshipSelect
        label="Relationship"
        LabelIcon={LinkIcon}
        selectedHousehold={household}
        selectedContact={contact}
        selectedBusiness={business}
        showPeekView
        setSelectedHouseholdId={(householdId) => {
          const update: Partial<Task> & { id: string } = { id: task.id! }
          if (householdId) {
            update.household_id = householdId
            update.taskable_type = "Household"
            update.taskable_id = householdId
            update.contact_id = null
          } else {
            update.household_id = null
            update.taskable_type = null
            update.taskable_id = null
          }
          updateTask(update)
        }}
        setSelectedContactId={(contactId) => {
          const update: Partial<Task> & { id: string } = { id: task.id! }
          if (contactId) {
            update.contact_id = contactId
            update.taskable_type = "Contact"
            update.taskable_id = contactId
            update.household_id = null
          } else {
            update.contact_id = null
            update.taskable_type = null
            update.taskable_id = null
          }
          updateTask(update)
        }}
        setSelectedBusinessId={(businessId) => {
          const update: Partial<Task> & { id: string } = { id: task.id! }
          if (businessId) {
            update.household_id = null
            update.contact_id = null
            update.taskable_type = "Business"
            update.taskable_id = businessId
          } else {
            update.household_id = null
            update.contact_id = null
            update.taskable_type = null
            update.taskable_id = null
          }
          updateTask(update)
        }}
        onSave={async (householdId, contactId, businessId) => {
          const update: Partial<Task> & { id: string } = { id: task.id! }
          if (contactId) {
            update.contact_id = contactId
            update.taskable_type = "Contact"
            update.taskable_id = contactId
            update.household_id = null
          } else if (householdId) {
            update.household_id = householdId
            update.taskable_type = "Household"
            update.taskable_id = householdId
            update.contact_id = null
          } else if (businessId) {
            update.household_id = null
            update.contact_id = null
            update.taskable_type = "Business"
            update.taskable_id = businessId
          } else {
            update.household_id = null
            update.contact_id = null
            update.taskable_type = null
            update.taskable_id = null
          }
          updateTask(update)
        }}
        isDisabled={isCompleted}
        fixedLabelWidth="120px"
        allowBusinesses
        bookId={isAllBooksMode ? task.book_id : undefined}
      />
      <InlineDatePicker
        label="Due date"
        LabelIcon={CalendarIcon}
        defaultValue={dueDateValue}
        placeholder="None"
        disabled={isCompleted}
        includeTime
        showTime={showDueTime}
        onShowTimeChange={(hasTime) => {
          setShowDueTime(hasTime)
          updateTask({ id: task.id!, all_day: !hasTime })
        }}
        onTimeChange={(value, hasTime) => {
          // Persist the deadline and all-day flag in a single PUT so they can't race and
          // null the deadline. Uses `hasTime` directly rather than the closed-over
          // showDueTime, which would be stale during a time/all-day change.
          setShowDueTime(hasTime)
          return updateTask({
            id: task.id!,
            deadline_at: hasTime ? value : dateAtStartOfDayInTimeZone(value, bookTimezone),
            all_day: !hasTime,
          })
        }}
        onSave={(value: Date | null) => {
          if (value && !isValidDate(value)) return
          return updateTask({
            id: task.id!,
            deadline_at: value && !showDueTime ? dateAtStartOfDayInTimeZone(value, bookTimezone) : value,
          })
        }}
        fixedLabelWidth="120px"
      />

      <InlineDatePicker
        label="Last updated"
        LabelIcon={CalendarIcon}
        value={updatedAt}
        placeholder="-"
        disabled
        clearable={false}
        fixedLabelWidth="120px"
        showTime
      />

      <InlineDatePicker
        label="Created"
        LabelIcon={CalendarIcon}
        value={createdAt}
        placeholder="-"
        disabled
        clearable={false}
        fixedLabelWidth="120px"
      />

      <BaseInlineInput label="Repeats" LabelIcon={Repeat} fixedLabelWidth="120px">
        {task.task_recurrence ? (
          <div className="flex items-center gap-2 px-2">
            <TaskRepeatBadge task={task} size="sm" disabled={isCompleted} />
            {task.task_recurrence.active && task.task_recurrence.has_unpropagated_changes && !isCompleted && (
              <Button variant="outline" size="xs" onClick={() => setShowApplyConfirm(true)}>
                Apply
              </Button>
            )}
          </div>
        ) : (
          <MakeRecurringButton task={task} disabled={isCompleted} />
        )}
      </BaseInlineInput>

      <InlineMentionEditor
        mentions={task.mentions || []}
        onChange={(refs) => {
          updateTask({
            id: task.id!,
            mentions: refs,
          })
        }}
        LabelIcon={Link2}
        disabled={isCompleted}
        fixedLabelWidth="120px"
        bookId={isAllBooksMode ? task.book_id : undefined}
      />

      <BaseInlineInput label="Project" LabelIcon={FolderKanban} fixedLabelWidth="120px">
        {task.project ? (
          <div className="flex items-center gap-1 px-2">
            <PeekView recordType="Project" recordId={task.project.id} label={task.project.name} size="sm" />
            {!isCompleted && (
              <button
                onClick={() => setShowRemoveProjectConfirm(true)}
                className="text-muted-foreground hover:text-foreground p-0.5"
                type="button"
                aria-label="Remove from project"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ) : !isCompleted ? (
          <Popover
            open={isProjectPopoverOpen}
            onOpenChange={(open) => {
              setIsProjectPopoverOpen(open)
              if (!open) setProjectSearch("")
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-primary flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2 text-sm focus:outline-1"
              >
                <Plus className="size-3.5" />
                Add to project
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput placeholder="Search projects..." value={projectSearch} onValueChange={setProjectSearch} />
                <CommandList>
                  <CommandEmpty className="text-muted-foreground p-2 text-center text-xs">
                    No active projects
                  </CommandEmpty>
                  <ScrollArea className="h-full">
                    <CommandGroup className="max-h-64 p-1">
                      {activeProjects.map((project) => (
                        <CommandItem
                          key={project.id}
                          value={project.id}
                          onSelect={() => {
                            handleProjectChange(project.id, project.project_milestone_id)
                            setIsProjectPopoverOpen(false)
                            setProjectSearch("")
                          }}
                          className="cursor-pointer"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{project.name}</span>
                            {project.project_template?.title && (
                              <span className="text-muted-foreground text-xs">{project.project_template.title}</span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </ScrollArea>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : null}
      </BaseInlineInput>

      <TaskAttachments task={task} isCompleted={isCompleted} />

      {isCompleted && (
        <ReadonlyAttribute
          label="Completed"
          LabelIcon={CheckCircle}
          value={completedValue}
          placeholder="-"
          fixedLabelWidth="120px"
        />
      )}

      <ConfirmDialog
        open={showRemoveProjectConfirm}
        onOpenChange={setShowRemoveProjectConfirm}
        title={"Remove from project?"}
        description={"This task will be removed from the project but will not be deleted."}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowRemoveProjectConfirm(false) }}
        action={{ label: "Remove", onClick: handleRemoveFromProject }}
      />

      <ConfirmDialog
        open={showApplyConfirm}
        onOpenChange={setShowApplyConfirm}
        title={"Apply changes to series?"}
        description={
          "This will update the recurring template and all future scheduled tasks to match this task's current settings."
        }
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowApplyConfirm(false) }}
        action={{ label: "Apply", onClick: handlePropagateRecurrence }}
      />
    </div>
  )
}
